import React, { useState } from 'react';
import { trackingAPI } from '../lib/api';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Search, 
  Package, 
  Truck, 
  ExternalLink,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { packageStatusConfig, formatDate, formatCurrency, countryNames } from '../lib/utils';

export default function TrackingPage() {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [packages, setPackages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!trackingNumber.trim()) {
      toast.error('Veuillez entrer un numéro de suivi');
      return;
    }

    setLoading(true);
    setSearched(true);
    
    try {
      const res = await trackingAPI.track(trackingNumber.trim());
      setPackages(res.data.packages);
    } catch (error) {
      setPackages([]);
      if (error.response?.status === 404) {
        // No packages found, will show empty state
      } else {
        toast.error('Erreur lors de la recherche');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="tracking-page">
      <Navbar />
      
      <main className="pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-heading font-bold text-4xl md:text-5xl text-slate-900 mb-4">
              Suivre un colis
            </h1>
            <p className="text-lg text-slate-600 max-w-xl mx-auto">
              Entrez votre numéro client (ex: CB-XXXXXX) pour voir tous vos colis, 
              ou un ID de colis spécifique.
            </p>
          </div>

          {/* Search Form */}
          <Card className="bg-white border-slate-200 mb-8">
            <CardContent className="p-6">
              <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Numéro client (CB-XXXXXX) ou ID colis"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    className="pl-12 h-14 text-lg"
                    data-testid="tracking-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="h-14 px-8 bg-blue-600 hover:bg-blue-700 text-white font-bold"
                  disabled={loading}
                  data-testid="tracking-search-btn"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                      Recherche...
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5 mr-2" />
                      Rechercher
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Results */}
          {searched && (
            <div className="space-y-4">
              {packages === null || packages.length === 0 ? (
                <Card className="bg-white border-slate-200">
                  <CardContent className="p-12 text-center">
                    <AlertCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">
                      Aucun colis trouvé
                    </h3>
                    <p className="text-slate-600">
                      Vérifiez votre numéro de suivi et réessayez.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading font-bold text-xl text-slate-900">
                      {packages.length} colis trouvé{packages.length > 1 ? 's' : ''}
                    </h2>
                  </div>
                  
                  {packages.map((pkg, index) => (
                    <TrackingCard key={pkg.id} pkg={pkg} index={index} />
                  ))}
                </>
              )}
            </div>
          )}

          {/* Info Section */}
          {!searched && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <Card className="bg-white border-slate-200">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Package className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-heading font-bold text-lg mb-2">Reçu</h3>
                  <p className="text-sm text-slate-600">
                    Votre colis est arrivé dans notre entrepôt
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-slate-200">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <RefreshCw className="w-6 h-6 text-yellow-600" />
                  </div>
                  <h3 className="font-heading font-bold text-lg mb-2">En préparation</h3>
                  <p className="text-sm text-slate-600">
                    Nous préparons votre colis pour l'expédition
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-slate-200">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Truck className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="font-heading font-bold text-lg mb-2">Expédié</h3>
                  <p className="text-sm text-slate-600">
                    Votre colis est en route vers sa destination
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

function TrackingCard({ pkg, index }) {
  const statusConfig = packageStatusConfig[pkg.status] || packageStatusConfig.received;

  return (
    <Card 
      className="bg-white border-slate-200 overflow-hidden animate-fade-in"
      style={{ animationDelay: `${index * 100}ms` }}
      data-testid={`tracking-result-${pkg.id}`}
    >
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg text-slate-900">
                  {pkg.description}
                </h3>
                <p className="text-sm text-slate-500">{pkg.customer_number}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
              <div>
                <p className="text-slate-500">Poids</p>
                <p className="font-medium text-slate-900">{pkg.weight} kg</p>
              </div>
              <div>
                <p className="text-slate-500">Dimensions</p>
                <p className="font-medium text-slate-900">{pkg.length}x{pkg.width}x{pkg.height} cm</p>
              </div>
              <div>
                <p className="text-slate-500">Destination</p>
                <p className="font-medium text-slate-900">{countryNames[pkg.destination_country] || pkg.destination_country}</p>
              </div>
              <div>
                <p className="text-slate-500">Reçu le</p>
                <p className="font-medium text-slate-900">{formatDate(pkg.created_at)}</p>
              </div>
            </div>

            {/* Status Timeline */}
            <div className="flex items-center gap-2 mb-4">
              <Badge className={statusConfig.color}>
                {statusConfig.label}
              </Badge>
              {pkg.paid && (
                <Badge className="bg-lime-100 text-lime-800">Payé</Badge>
              )}
            </div>

            {pkg.tracking_url && (
              <a 
                href={pkg.tracking_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
              >
                <Truck className="w-4 h-4" />
                Suivre sur le site du transporteur
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>

          <div className="text-right">
            <p className="text-sm text-slate-500 mb-1">Frais d'expédition</p>
            <p className="font-heading font-bold text-2xl text-slate-900">
              {formatCurrency(pkg.shipping_cost)}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
